import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { User } from "oidc-client";

import { HeaderAccountComponent } from "./header-account/header-account.component";
import { AuthenticationService } from "../../core/core";
import { HeaderCartComponent } from "./header-cart/header-cart.component";
import { HeaderListMachineComponent } from "./header-list-machine/header-list-machine.component";
import { HeaderLeftSideComponent } from "./header-left-side/header-left-side.component";

@Injectable({
  providedIn: "root",
})
export class HeaderService {
  user: User;

  rightSideHeaderItems = new BehaviorSubject([]);
  leftSideHeaderItems = new BehaviorSubject([]);

  // Controls visibility of the global search bar
  showGlobalSearchBar = new BehaviorSubject<boolean>(true);

  // Whether to render the mobile-only chip row + dropdown sheet in the left-side header.
  // Only enabled on the home page and all search-results routes; off everywhere else so
  // we don't add UI noise to product, manage, checkout, etc. pages on mobile.
  showMobileLeftSideDropdowns = new BehaviorSubject<boolean>(false);

  // Heading text shown in the mobile global search bar (e.g. "Construction Equipment Rentals in Carlsbad, CA")
  mobileSearchHeading = new BehaviorSubject<string>("");

  // Breadcrumb segments for the mobile global search bar
  mobileSearchBreadcrumbs = new BehaviorSubject<{ label: string; url?: string }[]>([]);

  lastMethodCalled: string;

  constructor(private authenticationService: AuthenticationService) {
    this.authenticationService.user$.subscribe(user => {
      this.user = user;
      // this is such a hack and the whole header needs to be reworked to not be so custom and not have dynamic options when on different pages.
      // since it could take a bit for the user to get signed in silent we want to be able to recall the last method so it can determine what to show on the header
      if (this.lastMethodCalled) {
        this[this.lastMethodCalled]();
      }
    });
  }

  showHomePageHeaderItems() {
    const rightSideItems = [HeaderListMachineComponent] as any[];

    this.user ? rightSideItems.push(HeaderAccountComponent, HeaderCartComponent) : rightSideItems.push(HeaderAccountComponent, HeaderCartComponent);

    this.rightSideHeaderItems.next(rightSideItems);
    // Always show navigation links (Equipment Rentals, Buy It Now, Rent to Own, phone)
    this.leftSideHeaderItems.next([HeaderLeftSideComponent]);
    // Homepage already has its own search, hide the global search bar
    this.showGlobalSearchBar.next(false);
    // Mobile chip-row dropdowns are enabled on the home page.
    this.showMobileLeftSideDropdowns.next(true);

    this.lastMethodCalled = "showHomePageHeaderItems";
  }

  showSearchResultsHeaderItems() {
    const rightSideItems = [HeaderListMachineComponent, HeaderAccountComponent, HeaderCartComponent] as any[];
    this.rightSideHeaderItems.next(rightSideItems);
    // Always show navigation links
    this.leftSideHeaderItems.next([HeaderLeftSideComponent]);
    // Show global search bar on search results pages
    this.showGlobalSearchBar.next(true);
    // Mobile chip-row dropdowns are enabled on all search-results routes.
    this.showMobileLeftSideDropdowns.next(true);

    this.lastMethodCalled = "showSearchResultsHeaderItems";
  }

  showDefaultHeaderItems() {
    const rightSideItems = [HeaderListMachineComponent] as any[];

    this.user ? rightSideItems.push(HeaderAccountComponent, HeaderCartComponent) : rightSideItems.push(HeaderAccountComponent, HeaderCartComponent);

    this.rightSideHeaderItems.next(rightSideItems);
    // Always show navigation links instead of search (search is now in global search bar)
    this.leftSideHeaderItems.next([HeaderLeftSideComponent]);
    this.showGlobalSearchBar.next(true);
    this.showMobileLeftSideDropdowns.next(false);

    this.lastMethodCalled = "showDefaultHeaderItems";
  }

  showCommerceHeaderItems() {
    this.showDefaultHeaderItems();
    // Catalog pages provide their own product search instead of the machine/location search.
    this.showGlobalSearchBar.next(false);
    this.showMobileLeftSideDropdowns.next(true);
    this.lastMethodCalled = "showCommerceHeaderItems";
  }

  showManageHeaderItems() {
    const rightSideItems = [HeaderListMachineComponent] as any[];

    this.user ? rightSideItems.push(HeaderAccountComponent, HeaderCartComponent) : rightSideItems.push(HeaderAccountComponent, HeaderCartComponent);

    this.rightSideHeaderItems.next(rightSideItems);
    this.leftSideHeaderItems.next([HeaderLeftSideComponent]);
    this.showGlobalSearchBar.next(false);
    this.showMobileLeftSideDropdowns.next(false);

    this.lastMethodCalled = "showManageHeaderItems";
  }

  showCheckoutHeaderItems() {
    const rightSideItems = [HeaderListMachineComponent] as any[];

    this.user ? rightSideItems.push(HeaderAccountComponent, HeaderCartComponent) : rightSideItems.push(HeaderAccountComponent, HeaderCartComponent);

    this.rightSideHeaderItems.next(rightSideItems);
    // Show navigation links but hide global search bar during checkout
    this.leftSideHeaderItems.next([HeaderLeftSideComponent]);
    this.showGlobalSearchBar.next(false);
    this.showMobileLeftSideDropdowns.next(false);

    this.lastMethodCalled = "showCheckoutHeaderItems";
  }

  /** Minimal native Shop checkout chrome, independent of equipment request navigation. */
  showShopCheckoutHeaderItems() {
    if (!document.body.classList.contains("rubbl-app-shell")) {
      this.showCommerceHeaderItems();
      this.lastMethodCalled = "showShopCheckoutHeaderItems";
      return;
    }
    this.rightSideHeaderItems.next([HeaderAccountComponent, HeaderCartComponent]);
    this.leftSideHeaderItems.next([]);
    this.showGlobalSearchBar.next(false);
    this.showMobileLeftSideDropdowns.next(false);
    this.lastMethodCalled = "showShopCheckoutHeaderItems";
  }

  showAuthHeaderItems() {
    const rightSideItems = [HeaderListMachineComponent] as any[];

    this.user ? rightSideItems.push(HeaderAccountComponent, HeaderCartComponent) : rightSideItems.push(HeaderAccountComponent, HeaderCartComponent);

    this.rightSideHeaderItems.next(rightSideItems);
    this.leftSideHeaderItems.next([HeaderLeftSideComponent]);
    this.showGlobalSearchBar.next(false);
    this.showMobileLeftSideDropdowns.next(false);

    this.lastMethodCalled = "showAuthHeaderItems";
  }

  showAdminHeaderItems() {
    this.rightSideHeaderItems.next([HeaderAccountComponent]);
    this.showGlobalSearchBar.next(false);
    this.showMobileLeftSideDropdowns.next(false);
    this.lastMethodCalled = "showAdminHeaderItems";
  }
}
